// src/pages/sm/ShiftMaster.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControlLabel, Switch, Grid, Stack, Tooltip,
    Chip, Divider, Paper, Fade
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    Fastfood as FastfoodIcon,
    DirectionsBus as TransportIcon,
    Timer as TimerIcon,
    Nightlight as SpecialIcon,
    Payments as PaymentsIcon,
    Settings as SettingsIcon,
    InfoOutlined as InfoIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const ShiftMaster = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [shifts, setShifts] = useState([]);
    const [, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingShift, setEditingShift] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        shift_code: '',
        shift_name: '',
        start_time: '08:00',
        end_time: '17:00',
        is_overnight: 0,
        break_minutes: 60,
        late_grace_minutes: 15,
        early_leave_grace_minutes: 0,
        ot_grace_minutes: 30,
        meal_allowance: 0,
        meal_allowance_min_hours: 0,
        transport_allowance: 0,
        transport_allowance_min_hours: 0,
        special_allowance: 0,
        special_allowance_min_hours: 0,
        ot_allowance: 0,
        ot_allowance_min_hours: 0,
        is_active: 1
    });

    const tenant = React.useMemo(() => localStorage.getItem('tenant'), []);

    // Global Settings State
    const [globalSettings, setGlobalSettings] = useState({
        include_grace_period_in_late_mins: 1,
        include_grace_period_in_early_leave_mins: 1
    });
    const [savingSettings, setSavingSettings] = useState(false);

    const fetchSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/settings`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success && res.data.data) {
                setGlobalSettings(prev => ({ ...prev, ...res.data.data }));
            }
        } catch (err) {
            console.error("Error fetching settings:", err);
        }
    }, [tenant]);

    const handleSettingsChange = async (name, checked) => {
        const newValue = checked ? 1 : 0;
        setGlobalSettings(prev => ({ ...prev, [name]: newValue }));
        setSavingSettings(true);
        try {
            const updated = { ...globalSettings, [name]: newValue };
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/settings`, updated, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
        } catch (err) {
            console.error("Error updating settings:", err);
            alert("Error saving settings");
            setGlobalSettings(prev => ({ ...prev, [name]: checked ? 0 : 1 })); // Revert on fail
        } finally {
            setSavingSettings(false);
        }
    };

    const fetchShifts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/shifts`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                setShifts(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching shifts:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchShifts();
        fetchSettings();
    }, [fetchShifts, fetchSettings]);

    const handleOpenDialog = useCallback((shift = null) => {
        if (shift) {
            setEditingShift(shift);
            setFormData({
                shift_code: shift.shift_code,
                shift_name: shift.shift_name,
                start_time: shift.start_time.substring(0, 5),
                end_time: shift.end_time.substring(0, 5),
                is_overnight: shift.is_overnight,
                break_minutes: shift.break_minutes,
                late_grace_minutes: shift.late_grace_minutes,
                early_leave_grace_minutes: shift.early_leave_grace_minutes,
                ot_grace_minutes: shift.ot_grace_minutes ?? 30,
                meal_allowance: shift.meal_allowance || 0,
                meal_allowance_min_hours: shift.meal_allowance_min_hours || 0,
                transport_allowance: shift.transport_allowance || 0,
                transport_allowance_min_hours: shift.transport_allowance_min_hours || 0,
                special_allowance: shift.special_allowance || 0,
                special_allowance_min_hours: shift.special_allowance_min_hours || 0,
                ot_allowance: shift.ot_allowance || 0,
                ot_allowance_min_hours: shift.ot_allowance_min_hours || 0,
                is_active: shift.is_active
            });
        } else {
            setEditingShift(null);
            setFormData({
                shift_code: '',
                shift_name: '',
                start_time: '08:00',
                end_time: '17:00',
                is_overnight: 0,
                break_minutes: 60,
                late_grace_minutes: 15,
                early_leave_grace_minutes: 0,
                ot_grace_minutes: 30,
                meal_allowance: 0,
                meal_allowance_min_hours: 0,
                transport_allowance: 0,
                transport_allowance_min_hours: 0,
                special_allowance: 0,
                special_allowance_min_hours: 0,
                ot_allowance: 0,
                ot_allowance_min_hours: 0,
                is_active: 1
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingShift(null);
    }, []);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    // Calculations
    const workHoursInfo = useMemo(() => {
        if (!formData.start_time || !formData.end_time) return { total: 0, net: 0 };
        const start = new Date(`2000-01-01T${formData.start_time}`);
        let end = new Date(`2000-01-01T${formData.end_time}`);
        if (formData.is_overnight) end = new Date(`2000-01-02T${formData.end_time}`);
        
        const diffMs = end - start;
        const totalHrs = diffMs / (1000 * 60 * 60);
        const netHrs = totalHrs - (formData.break_minutes / 60);
        return { total: totalHrs.toFixed(1), net: netHrs.toFixed(1) };
    }, [formData.start_time, formData.end_time, formData.is_overnight, formData.break_minutes]);

    const handleSubmit = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/shifts${editingShift ? `/${editingShift.id}` : ''}`;
            const method = editingShift ? 'put' : 'post';

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });

            if (res.data.success) {
                fetchShifts();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving shift:", err);
            alert(err.response?.data?.message || "Error saving shift");
        }
    }, [editingShift, formData, fetchShifts, handleCloseDialog, tenant]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this shift?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/shifts/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                fetchShifts();
            }
        } catch (err) {
            console.error("Error deleting shift:", err);
            alert(err.response?.data?.message || "Error deleting shift");
        }
    }, [fetchShifts, tenant]);

    const handleLogout = () => {
        localStorage.clear();
        setIsLoggedIn(false);
        navigate('/login');
    };

    const columnDefs = useMemo(() => [
        {
            headerName: "Code",
            field: "shift_code",
            width: 130,
            cellStyle: { fontWeight: 700, color: 'primary.main' },
            pinned: 'left'
        },
        { headerName: "Name", field: "shift_name", flex: 1, minWidth: 200 },
        {
            headerName: "Timing",
            width: 180,
            cellRenderer: p => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
                    <Chip 
                        label={`${p.data.start_time.substring(0, 5)} - ${p.data.end_time.substring(0, 5)}`}
                        size="small"
                        icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ bgcolor: (theme) => `${theme.palette.primary.main}0d`, border: (theme) => `1px solid ${theme.palette.primary.main}33`, fontWeight: 600, color: 'primary.main' }}
                    />
                    {p.data.is_overnight === 1 && <Tooltip title="Overnight Shift"><Chip label="O/N" size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }} /></Tooltip>}
                </Stack>
            )
        },
        {
            headerName: "Rules",
            width: 240,
            cellRenderer: p => (
                <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                    <Tooltip title={`Break: ${p.data.break_minutes}m`}><Chip label={`${p.data.break_minutes}m`} size="small" icon={<FastfoodIcon sx={{ fontSize: '12px !important' }} />} variant="outlined" /></Tooltip>
                    <Tooltip title={`Late Grace: ${p.data.late_grace_minutes}m`}><Chip label={`${p.data.late_grace_minutes}m`} size="small" icon={<TimerIcon sx={{ fontSize: '12px !important' }} />} variant="outlined" color="error" /></Tooltip>
                    <Tooltip title={`OT Grace: ${p.data.ot_grace_minutes ?? 30}m`}><Chip label={`${p.data.ot_grace_minutes ?? 30}m`} size="small" icon={<TimerIcon sx={{ fontSize: '12px !important' }} />} variant="outlined" color="warning" /></Tooltip>
                </Stack>
            )
        },
        {
            headerName: "Daily Benefits",
            width: 280,
            cellRenderer: p => (
                <Grid container spacing={0.5} sx={{ py: 1 }}>
                    {p.data.meal_allowance > 0 && <Grid item><Chip label={`Meal: ${p.data.meal_allowance}`} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#059669', bgcolor: '#ecfdf5' }} /></Grid>}
                    {p.data.transport_allowance > 0 && <Grid item><Chip label={`Trans: ${p.data.transport_allowance}`} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#2563eb', bgcolor: '#eff6ff' }} /></Grid>}
                    {p.data.ot_allowance > 0 && <Grid item><Chip label={`OT: ${p.data.ot_allowance}`} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#ca8a04', bgcolor: '#fefce8' }} /></Grid>}
                </Grid>
            )
        },
        {
            headerName: "Status",
            field: "is_active",
            width: 120,
            cellRenderer: p => (
                <Chip
                    label={p.value === 1 ? "ACTIVE" : "OFFLINE"}
                    size="small"
                    sx={{ 
                        fontWeight: 800, fontSize: 10,
                        bgcolor: p.value === 1 ? '#ecfdf5' : '#f1f5f9',
                        color: p.value === 1 ? '#10b981' : '#64748b',
                        border: '1px solid',
                        borderColor: p.value === 1 ? '#d1fae5' : '#e2e8f0'
                    }}
                />
            )
        },
        {
            headerName: "Actions",
            width: 110,
            pinned: 'right',
            cellRenderer: p => (
                <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" onClick={() => handleOpenDialog(p.data)} sx={{ color: 'primary.main', '&:hover': { bgcolor: (theme) => `${theme.palette.primary.main}33` } }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(p.data.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' } }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )
        }
    ], [handleDelete, handleOpenDialog]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={handleLogout} />

            <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1 }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0 }}>
                                    Shift Master
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    Define and optimize personnel working schedules and operational timing
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    bgcolor: 'primary.main', borderRadius: 2.5, px: 3, py: 1, fontWeight: 700, textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                Create New Shift
                            </Button>
                        </Stack>

                        <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'hidden', boxShadow: '0 2px 10px 0 rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <Box sx={{ bgcolor: '#f8fafc', px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SettingsIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                                    Global Configuration
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <FormControlLabel
                                                control={<Switch disabled={savingSettings} size="small" checked={globalSettings.include_grace_period_in_late_mins === 1} onChange={(e) => handleSettingsChange('include_grace_period_in_late_mins', e.target.checked)} color="primary" />}
                                                label={<Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>Include Grace Period in Late Mins</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                            <Tooltip title="If enabled, lateness includes the grace period itself (e.g. 16m late with 15m grace = 16 mins). If disabled, it records 1 min.">
                                                <InfoIcon sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <FormControlLabel
                                                control={<Switch disabled={savingSettings} size="small" checked={globalSettings.include_grace_period_in_early_leave_mins === 1} onChange={(e) => handleSettingsChange('include_grace_period_in_early_leave_mins', e.target.checked)} color="primary" />}
                                                label={<Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>Include Grace Period in Early Leave Mins</Typography>}
                                                sx={{ m: 0 }}
                                            />
                                            <Tooltip title="If enabled, early leave includes the grace period itself. If disabled, the grace period is subtracted from the penalty.">
                                                <InfoIcon sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
                                            </Tooltip>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>

                        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 10px 0 rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 240px)', width: '100%' }}>
                                <AgGridReact
                                    rowData={shifts}
                                    columnDefs={columnDefs}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: true,
                                        resizable: true,
                                        cellStyle: { display: 'flex', alignItems: 'center', color: '#334155', fontWeight: 500 }
                                    }}
                                    pagination={true}
                                    paginationPageSize={15}
                                    animateRows={true}
                                    headerHeight={52}
                                    rowHeight={52}
                                />
                            </div>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            {/* SHIFT DIALOG */}
            <Dialog 
                open={openDialog} 
                onClose={handleCloseDialog} 
                maxWidth="md" 
                fullWidth 
                TransitionComponent={Fade}
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: (theme) => `${theme.palette.primary.main}1a`, color: 'primary.main', display: 'flex' }}>
                            <SettingsIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                {editingShift ? "Refine Shift Configuration" : "Establish New Work Shift"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Set precise timing, compliance rules, and monetary triggers</Typography>
                        </Box>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 4, pt: 5 }}>
                    <Grid container spacing={5}>
                        {/* LEFT COL: BASIS */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={4}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 18, color: 'primary.main' }} /> Digital Identity
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <TextField fullWidth label="Code" name="shift_code" value={formData.shift_code} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                        </Grid>
                                        <Grid item xs={8}>
                                            <TextField fullWidth label="Operational Name" name="shift_name" value={formData.shift_name} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} /> Timing Matrix
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Expected In" type="time" name="start_time" value={formData.start_time} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Expected Out" type="time" name="end_time" value={formData.end_time} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 2.5, borderRadius: 3.5, border: '1px solid #e0e7ff', bgcolor: '#f5f7ff' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main' }}>WORK SPAN ANALYSIS</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main' }}>{workHoursInfo.net} HOURS NET</Typography>
                                                </Stack>
                                                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e7ff', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                                                    <Box sx={{ position: 'absolute', left: '10%', right: '15%', top: 0, bottom: 0, bgcolor: 'primary.main', borderRadius: 4 }} />
                                                </Box>
                                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>00:00</Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>12:00</Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>24:00</Typography>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Overnight Rotation</Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Shift spans across calendar days.</Typography>
                                                </Box>
                                                <Switch checked={formData.is_overnight === 1} onChange={handleChange} name="is_overnight" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f59e0b' } }} />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TimerIcon sx={{ fontSize: 18, color: 'primary.main' }} /> Compliance Buffers
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3}><TextField fullWidth label="Break (m)" type="number" name="break_minutes" value={formData.break_minutes} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Grid>
                                        <Grid item xs={3}><TextField fullWidth label="Late (m)" type="number" name="late_grace_minutes" value={formData.late_grace_minutes} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Grid>
                                        <Grid item xs={3}><TextField fullWidth label="Early (m)" type="number" name="early_leave_grace_minutes" value={formData.early_leave_grace_minutes} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Grid>
                                        <Grid item xs={3}><TextField fullWidth label="OT Grace (m)" type="number" name="ot_grace_minutes" value={formData.ot_grace_minutes} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} /></Grid>
                                    </Grid>
                                </Box>
                            </Stack>
                        </Grid>

                        {/* RIGHT COL: BENEFITS */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ height: '100%' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PaymentsIcon sx={{ fontSize: 18, color: 'primary.main' }} /> Monetary Incentives
                                </Typography>
                                <Stack spacing={2.5}>
                                    {[
                                        { label: 'Meal Allowance', amt: 'meal_allowance', hrs: 'meal_allowance_min_hours', icon: <FastfoodIcon fontSize="small" />, color: '#10b981' },
                                        { label: 'Transport Stipend', amt: 'transport_allowance', hrs: 'transport_allowance_min_hours', icon: <TransportIcon fontSize="small" />, color: '#3b82f6' },
                                        { label: 'OT Meal Tier', amt: 'ot_allowance', hrs: 'ot_allowance_min_hours', icon: <FastfoodIcon fontSize="small" />, color: '#f59e0b' },
                                        { label: 'Special Premium', amt: 'special_allowance', hrs: 'special_allowance_min_hours', icon: <SpecialIcon fontSize="small" />, color: 'primary.main' }
                                    ].map((item, idx) => (
                                        <Paper key={idx} variant="outlined" sx={{ p: 2.5, borderRadius: 4, border: '1.5px solid #f1f5f9', bgcolor: 'transparent' }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                                <Box sx={{ color: item.color, display: 'flex' }}>{item.icon}</Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>{item.label}</Typography>
                                            </Stack>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}><TextField fullWidth size="small" label="Amount (MMK)" type="number" name={item.amt} value={formData[item.amt]} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} /></Grid>
                                                <Grid item xs={6}><TextField fullWidth size="small" label="Min. Service (Hrs)" type="number" name={item.hrs} value={formData[item.hrs]} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} /></Grid>
                                            </Grid>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            sx={{ ml: 1 }}
                            control={<Switch size="small" color="success" name="is_active" checked={formData.is_active === 1} onChange={handleChange} />}
                            label={<Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>Active Status</Typography>}
                        />
                        <Stack direction="row" spacing={1.5}>
                            <Button onClick={handleCloseDialog} sx={{ color: '#64748b', fontWeight: 700, px: 3, textTransform: 'none' }}>Discard</Button>
                            <Button 
                                variant="contained" 
                                onClick={handleSubmit}
                                sx={{ 
                                    bgcolor: 'primary.main', fontWeight: 700, borderRadius: 2.5, px: 4, py: 1, textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                Commit Configuration
                            </Button>
                        </Stack>
                    </Stack>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShiftMaster;
